import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useNextStep } from "nextstepjs";
import { useAtomValue } from "jotai";
import { useTourLayoutRefresh } from "@/components/nextstep-bridge";
import {
  useStats,
  useShareMissions,
  useTheme,
  useStockList,
  useTokenList,
} from "@/lib/hooks/useApi";
import { useHydrateActiveSeeds } from "@/lib/hooks/useActiveMatchmaking";
import { useBalance } from "@/lib/hooks/useBalance";
import { useFindMatch } from "@/lib/hooks/useFindMatch";
import { MatchmakingPills } from "@/components/MatchmakingFloating";
import { toast } from "sonner";
import { equippedTokenAtom } from "@/lib/atoms";
import { useSetAtom } from "jotai";
import {
  setHomeUiReadyAtom,
  teleportDialogOpenAtom,
  welcomePackDialogOpenAtom,
} from "@/lib/homeUiAtoms";
import { trackSocialBonusClick } from "@/lib/analytics/tournament-winner";
import { DefaultButton } from "@/components/DefaultButton";
import { Spinner } from "@/components/ui/spinner";
import { EquipTokenSheetContent } from "@/components/EquipTokenSheetContent";
import { useState, useEffect, useMemo, useRef } from "react";
import ResourceBar from "@/components/ResourceBar";
import { LoginGate } from "@/components/LoginGate";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useTranslation } from "@/lib/hooks/useTranslation";
import {
  cn,
  generateTextShadow,
  pixelCornerClipPath,
  USDC_ICON,
} from "@/lib/utils";
import { ShinyWavyText } from "@/components/ShinyWavyText";
import { SponsorDialog } from "@/components/SponsorDialog";
import { ItemDoubleTrophyDialog } from "@/components/ItemDoubleTrophyDialog";
import DoubleTrophyButton from "@/components/DoubleTrophyButton";
import { ItemChampionshipDialog } from "@/components/ItemChampionshipDialog";
import ChampionshipButton from "@/components/ChampionshipButton";
import { ItemPopUpNotifDialog } from "@/components/ItemPopUpNotifDialog";
import FreeGachaDialogButton from "@/components/FreeGachaDialogButton";
import { ItemRouletteDialog } from "@/components/ItemRouletteDialog";
import LuckyDrawButton from "@/components/LuckyDrawButton";
import InstantSwapButton from "@/components/InstantSwapButton";
import { useDailyTrophyBonusUnclaimed } from "@/components/DailyTrophyBonusButton";
import { WinnerListContainer } from "@/components/WinnerListContainer";
import { TournamentWinnerDialogs } from "@/components/TournamentWinnerDialog";
import {
  RookieMissionDialog,
  RookieMissionBadgeCount,
} from "@/components/RookieMissionHud";
import { MilestoneSheet } from "@/components/MilestoneSheet";
import { StepDialog } from "@/components/StepDialog";
import {
  getOnboardingLoggedInDialogSteps,
  getOnboardingLoggedOutDialogSteps,
} from "@/lib/constants";

import Marquee from "react-fast-marquee";
import {
  useGlobalWorldLuckyDraw,
  useGlobalWorldMarquee,
} from "@/lib/hooks/useGlobalWorldState";
import { t as tStatic } from "@/lib/i18n";
import { usePrivyCustom } from "@/lib/hooks/usePrivyCustom";
import dayjs from "dayjs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { PixelRedDot } from "@/components/PixelRedDot";

const FEE_CHIP_CLIP_OUTER = pixelCornerClipPath(2);
const FEE_CHIP_CLIP_INNER = pixelCornerClipPath(2, undefined, 2);

const MARQUEE_MODE_ROUTES: Record<string, string> = {
  rush: "/rush",
  grand_rush: "/grand-rush",
  wta: "/winner-takes-all",
  koth: "/koth",
};

const MARQUEE_MODE_LABELS: Record<string, string> = {
  rush: tStatic("hotspot.name.rush"),
  grand_rush: tStatic("hotspot.name.grandRush"),
  wta: tStatic("hotspot.name.winnerTakesAll"),
  koth: tStatic("hotspot.name.koth"),
};

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });

export const Route = createFileRoute("/(game)/home")({
  component: Basecamp,
});

function Basecamp() {
  const navigate = useNavigate();
  const { ready, user, login } = usePrivyCustom();
  const {
    startNextStep,
    closeNextStep,
    currentTour,
    currentStep,
    setCurrentStep,
  } = useNextStep();
  const { refreshTourLayout } = useTourLayoutRefresh();
  const { t } = useTranslation();
  const [isEquipSheetOpen, setIsEquipSheetOpen] = useState(false);
  const [entryFee, setEntryFee] = useState(3);
  const [isFeePopoverOpen, setIsFeePopoverOpen] = useState(false);

  const equipped = useAtomValue(equippedTokenAtom);
  const setTeleportDialogOpen = useSetAtom(teleportDialogOpenAtom);
  const setHomeUiReady = useSetAtom(setHomeUiReadyAtom);
  const isWelcomePackDialogOpen = useAtomValue(welcomePackDialogOpenAtom);

  useEffect(() => {
    setHomeUiReady(true);
  }, [setHomeUiReady]);

  // ── Stock-market fallback ─────────────────────────────────────────────
  // If the user has a stock equipped (persisted in localStorage) and the US
  // market is closed, the on-chain match would fail. Transparently substitute
  // BTC for the play action. The original stock equip stays in storage so it
  // auto-restores when the market reopens.
  const isStockEquipped = equipped?.token.assetType === "stocks";
  const { data: stockData } = useStockList(1, 120, {
    enabled: isStockEquipped,
  });
  const marketStatus = stockData?.marketStatus ?? null;
  const isStaleStock =
    isStockEquipped && marketStatus !== null && !marketStatus.isOpen;
  const { data: cryptoTokens } = useTokenList(1, 120, "crypto");
  const fallbackToken = useMemo(() => {
    if (!isStaleStock || !cryptoTokens || cryptoTokens.length === 0) {
      return null;
    }
    return (
      cryptoTokens.find((t) => t.tokenSymbol?.toUpperCase() === "BTC") ??
      cryptoTokens[0]
    );
  }, [isStaleStock, cryptoTokens]);
  const effectiveEquipped = useMemo(
    () =>
      isStaleStock && fallbackToken
        ? { token: fallbackToken, direction: equipped!.direction }
        : equipped,
    [isStaleStock, fallbackToken, equipped],
  );

  // Notify once per mount when the fallback engages, so the swap isn't silent.
  const fallbackNotifiedRef = useRef(false);
  useEffect(() => {
    if (isStaleStock && fallbackToken && !fallbackNotifiedRef.current) {
      fallbackNotifiedRef.current = true;
      toast.warning("Stock market is closed", {
        description: `Playing with ${fallbackToken.tokenSymbol.toUpperCase()}, or pick any other crypto for now. Your stock pick will return when the market reopens.`,
      });
    }
  }, [isStaleStock, fallbackToken]);

  // Matchmaking — shared flow (rate-limit cooldown, multi-match cap, sponsored
  // preview + commit, navigation). Pass effectiveEquipped so the stock-market
  // fallback flows through without mutating the persisted atom.
  const isFallbackLoading = isStaleStock && !fallbackToken;
  const {
    findMatch,
    disabled: findMatchHookDisabled,
    isSubmitting: isSubmittingMatchmaking,
    isSponsoredEligible,
    isPreviewingSponsor,
    isFreeConfirmOpen,
    previewedSponsor,
    confirmFreeMatch,
    dismissFreeConfirm,
  } = useFindMatch({
    equippedOverride: effectiveEquipped,
    isPending: isFallbackLoading,
    onMissingEquip: () => startNextStep("lobbySquadTour"),
  });

  const dailyBonusUnclaimed = useDailyTrophyBonusUnclaimed("vip");
  const luckyDrawBonusUnclaimed = useDailyTrophyBonusUnclaimed("lucky-draw");

  const { data: theme } = useTheme();
  const isChampionshipActive = theme?.championship.status.isActive ?? false;
  const campaignDate = isChampionshipActive
    ? theme?.championship.campaignDate
    : undefined;
  const nowSeconds = Date.now() / 1000;
  const isChampionshipOpen = campaignDate
    ? nowSeconds >= campaignDate.open
    : false;
  const isChampionshipStarted = campaignDate
    ? nowSeconds >= campaignDate.started
    : false;
  const isChampionshipEnded = campaignDate
    ? nowSeconds > campaignDate.ended
    : false;
  const walletAddress = user?.wallet?.address;
  const { data: statsData } = useStats(walletAddress ?? "");
  const userId = statsData?.userId;
  const { data: usdcBalance } = useBalance(statsData?.embeddedWallet ?? "");

  // Hydrate active matchmaking seeds from /history (live + waiting rooms only)
  useHydrateActiveSeeds(userId);

  // Sidebar data
  const luckyDraw = useGlobalWorldLuckyDraw();
  const marquee = useGlobalWorldMarquee();

  const { data: shareMissions, refetch: refetchShareMissions } =
    useShareMissions(userId);
  const hasXConnected = shareMissions?.some((m) => m.xHandle != null) ?? false;
  const [showWinnerDialogs, setShowWinnerDialogs] = useState(false);

  // Onboarding dialogs
  const [isLoggedOutOnboardingConsumed, setIsLoggedOutOnboardingConsumed] =
    useState(false);
  const [isPostLoginDialogOpen, setIsPostLoginDialogOpen] = useState(false);
  const [isLoginPending, setIsLoginPending] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const firstLoginDialogKey = walletAddress
    ? `cfl:first-login-dialog-shown:${walletAddress.toLowerCase()}`
    : null;
  const isOnboardingDialogOpen =
    ready && !user && !isLoggedOutOnboardingConsumed;

  const handleLoggedOutOnboardingAction = async () => {
    if (isLoginPending) return;

    setLoginError(null);
    setIsLoginPending(true);

    try {
      await waitForNextPaint();
      await login();
    } catch (error) {
      setLoginError(
        error instanceof Error && error.message
          ? error.message
          : t("stepDialog.loginFailed"),
      );
      setIsLoginPending(false);
    }
  };

  // Logged-out onboarding dialog
  useEffect(() => {
    if (user) {
      setIsLoggedOutOnboardingConsumed(false);
    }
  }, [user]);

  // First-login onboarding dialog
  useEffect(() => {
    if (!user || !firstLoginDialogKey) {
      setIsPostLoginDialogOpen(false);
      return;
    }
    try {
      if (localStorage.getItem(firstLoginDialogKey) === "1") return;
    } catch {
      return;
    }
    // Defer the post-login dialog while the welcome pack flow is active —
    // two simultaneously open Radix dialogs cause the later one to hijack
    // pointer events from the earlier one (the user can't click anything).
    if (isWelcomePackDialogOpen) return;
    if (statsData?.isWelcomePackEligible) return;
    const timeoutId = setTimeout(() => setIsPostLoginDialogOpen(true), 800);
    return () => clearTimeout(timeoutId);
  }, [
    user,
    firstLoginDialogKey,
    isWelcomePackDialogOpen,
    statsData?.isWelcomePackEligible,
  ]);

  const shouldShowChampionshipButton =
    isChampionshipOpen && !isChampionshipEnded;
  const shouldShowChampionshipAsLinkButton =
    shouldShowChampionshipButton && isChampionshipStarted;

  // Auto-trigger lobby tours on mount
  // Clear lobby tour flags on logout
  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem("cfl:lobby-tour-practice-pending");
      sessionStorage.removeItem("cfl:lobby-tour-squad-pending");
    }
  }, [user]);

  // Auto-trigger lobby tours — check on mount and whenever navigating back to /home
  useEffect(() => {
    if (!user) return;

    const check = () => {
      // Don't restart a tour that's already running — otherwise the effect
      // re-firing (e.g. when `startNextStep`'s identity changes) keeps
      // resetting the user back to step 0.
      if (currentTour) return;

      // Don't start any tour while the first-login dialog is open or pending
      if (isPostLoginDialogOpen) return;
      if (firstLoginDialogKey) {
        try {
          if (localStorage.getItem(firstLoginDialogKey) !== "1") return;
        } catch {
          return;
        }
      }

      // Don't start any tour while the welcome pack flow is active —
      // the dialog is eligible to open or already showing. Also wait for
      // stats to load so we don't race-start a tour before knowing.
      if (!walletAddress) return;
      if (!statsData) return;
      if (isWelcomePackDialogOpen) return;
      if (statsData.isWelcomePackEligible) return;

      // Squad tour takes priority over welcome tour
      const squadPending = sessionStorage.getItem(
        "cfl:lobby-tour-squad-pending",
      );
      if (squadPending) {
        sessionStorage.removeItem("cfl:lobby-tour-squad-pending");
        setTimeout(() => startNextStep("lobbySquadTour"), 500);
        return;
      }

      // Welcome tour for new users
      const done = localStorage.getItem("cfl:lobby-welcome-tour-done");
      if (done) return;
      setTimeout(() => startNextStep("lobbyWelcomeTour"), 500);
    };

    check();

    // Also listen for a custom event so navigating back from practice triggers the check
    const handler = () => check();
    window.addEventListener("lobbyTourCheck", handler);
    return () => window.removeEventListener("lobbyTourCheck", handler);
  }, [
    user,
    walletAddress,
    statsData,
    startNextStep,
    isPostLoginDialogOpen,
    firstLoginDialogKey,
    currentTour,
    isWelcomePackDialogOpen,
  ]);

  // If the welcome pack dialog opens while a lobby tour is already running,
  // close the tour so the dialog buttons aren't blocked by the tour overlay.
  useEffect(() => {
    if (!isWelcomePackDialogOpen) return;
    if (
      currentTour === "lobbyWelcomeTour" ||
      currentTour === "lobbySquadTour"
    ) {
      closeNextStep();
    }
  }, [isWelcomePackDialogOpen, currentTour, closeNextStep]);

  // Open teleport (modes) dialog when lobbyWelcomeTour advances to practice card step
  useEffect(() => {
    const handler = () => {
      setTeleportDialogOpen(true);
      // Refresh tour layout after dialog animation so nextstepjs can find the element.
      // Retry a couple of times in case the dialog content mounts after animation.
      setTimeout(() => refreshTourLayout(), 250);
      setTimeout(() => refreshTourLayout(), 600);
      setTimeout(() => refreshTourLayout(), 1000);
    };
    window.addEventListener("lobbyWelcomeTourOpenModes", handler);
    return () =>
      window.removeEventListener("lobbyWelcomeTourOpenModes", handler);
  }, [refreshTourLayout, setTeleportDialogOpen]);

  // Open equip sheet when lobbySquadTour advances to token list step
  useEffect(() => {
    const handler = () => {
      setIsEquipSheetOpen(true);
      setTimeout(() => refreshTourLayout(), 400);
    };
    window.addEventListener("lobbySquadTourOpenEquip", handler);
    return () => window.removeEventListener("lobbySquadTourOpenEquip", handler);
  }, [refreshTourLayout]);

  const playDisabled = findMatchHookDisabled;

  // Main home view
  return (
    <div className="relative flex flex-1 flex-col overflow-hidden home-wallpaper-background parallax-background pb-12">
      {/* Top bar */}
      <ResourceBar
        showAvatar
        showLuckyDrawTicket
        showVipTier
        showGachaCoin={false}
        showBadge={false}
        className="absolute top-4 left-0 right-1.5 flex items-start! justify-between! z-50 pl-1.5"
      />

      {/* Marquee */}
      {marquee.length > 0 && (
        <Marquee
          speed={50}
          className={cn(
            "absolute! top-20 left-0 w-full z-1 bg-black/60",
            "flex items-center border-y-[1.5px] border-yellow-400/75",
          )}
          style={{
            overflowX: "visible",
            maskImage:
              "linear-gradient(to right, transparent, black 10%, black 75%, transparent)",
            pointerEvents: "auto",
          }}
        >
          {marquee.map((item) => (
            <button
              key={item.id}
              className="relative mt-1.5 inline-flex items-center rounded-full pointer-events-auto overflow-visible"
              onClick={() => {
                const to = MARQUEE_MODE_ROUTES[item.mode];
                if (to) navigate({ to });
              }}
            >
              <WinnerListContainer
                imgUrl={item.profilePicture}
                decoration={item.activeFrameUrl ?? ""}
                size={40}
                className="w-auto mr-3 gap-1 sm:gap-1.5"
                username={item.username || "Anonymous"}
                prizeUsd={item.prizeUsd ?? 0}
                matchTypeLabel={MARQUEE_MODE_LABELS[item.mode] ?? item.mode}
                mode={item.mode}
                isNew={item.isNew}
                streak={item.streak}
                multiplier={item.multiplier}
                domainType={item.domainType}
              />
            </button>
          ))}
        </Marquee>
      )}

      {/* Sidebar Left */}
      <div
        className="absolute left-2 top-36 z-40 w-16 space-y-1"
        style={{ pointerEvents: "auto" }}
      >
        {/*
        {!!user && isRookie && (
          <PromoteTierDialog>
            <button
              type="button"
              className={cn(
                "relative w-full h-auto aspect-square z-10 default-btn-container hue-rotate-25 p-0! flex flex-col items-center justify-center cursor-pointer",
                "transition-transform duration-75 ease-linear",
                "hover:cursor-pointer active:scale-98 hover:brightness-95 hover:saturate-95",
              )}
            >
              <img
                src="https://cdn.navalabs.io/assets/badges/badge-pro_j8hYXt.png"
                alt="Promote"
                className="h-10 w-auto -hue-rotate-25"
                style={{ imageRendering: "pixelated" }}
              />
              <span className="relative text-xs text-shadow-black text-white font-black -mt-4 uppercase leading-none">
                Promote <br /> to Pro
              </span>
            </button>
          </PromoteTierDialog>
        )}
        */}

        {user ? (
          <ItemDoubleTrophyDialog
            doubleTrophiesExpiredDate={statsData?.doubleTrophiesExpiredDate}
          >
            <DoubleTrophyButton
              doubleTrophiesExpiredDate={statsData?.doubleTrophiesExpiredDate}
            />
          </ItemDoubleTrophyDialog>
        ) : null}

        {/* {shouldShowChampionshipButton && ( */}
        {/*   <ItemChampionshipDialog> */}
        {/*     <ChampionshipButton */}
        {/*       onClick={ */}
        {/*         shouldShowChampionshipAsLinkButton */}
        {/*           ? () => navigate({ to: "/championship" }) */}
        {/*           : undefined */}
        {/*       } */}
        {/*     /> */}
        {/*   </ItemChampionshipDialog> */}
        {/* )} */}

        {statsData?.gachaCoinEligibility?.isEligibleForGachaCoin && (
          <ItemPopUpNotifDialog
            headerText={t("homeCanvas.freeGachaCoin.headerText")}
            title={t("homeCanvas.freeGachaCoin.title")}
            description={t("homeCanvas.freeGachaCoin.description")}
            ctaLabel={t("homeCanvas.freeGachaCoin.ctaLabel")}
            onAction={() => navigate({ to: "/gacha" })}
            badgeImageSrc="https://cdn.navalabs.io/assets/gacha-machine_wdFBPO.png"
          >
            <FreeGachaDialogButton isGachaPage={false} />
          </ItemPopUpNotifDialog>
        )}

        <ItemRouletteDialog />

        {!!user && <InstantSwapButton />}

        <div className="relative">
          {!!user && luckyDrawBonusUnclaimed && (
            <PixelRedDot className="-top-1 -right-1" />
          )}
          <LuckyDrawButton
            startCountUp={true}
            prizeCount={Number(luckyDraw?.totalPrizeInUsd ?? 50)}
            participantCount={Number(luckyDraw?.participantsCount ?? 0)}
            onClick={() => navigate({ to: "/lucky-draw" })}
          />
        </div>
      </div>

      {/* Sidebar Right */}
      <div
        className="absolute right-2 top-36 z-40 w-16 space-y-1"
        style={{ pointerEvents: "auto" }}
      >
        <button
          type="button"
          className={cn(
            "relative w-full flex flex-col items-center justify-center",
            "transition-transform duration-75 ease-linear",
            "hover:cursor-pointer active:scale-98 hover:brightness-95 hover:saturate-95",
          )}
          onClick={() => {
            if (!user) {
              login();
              return;
            }
            navigate({ to: "/vip-program" });
          }}
        >
          {!!user && dailyBonusUnclaimed && (
            <PixelRedDot className="-top-1 -right-1" />
          )}
          <div className="w-full h-auto aspect-square z-10 dark-green-panel p-0 flex flex-col items-center justify-center">
            <img
              src="https://cdn.navalabs.io/assets/vip-icon_34gRDj.png"
              alt="VIP"
              className="w-10 h-auto"
              style={{ imageRendering: "pixelated" }}
            />
            <span
              className="text-xs text-white font-black -mt-3 uppercase leading-none"
              style={{ textShadow: generateTextShadow(0.15) }}
            >
              {t("homeCanvas.vipProgram.label")}
            </span>
          </div>
        </button>

        {/* First-login (rookie) missions — only meaningful while authed */}
        {!!user && (
          <RookieMissionDialog>
            <button
              type="button"
              className={cn(
                "relative w-full flex flex-col items-center justify-center",
                "transition-transform duration-75 ease-linear",
                "hover:cursor-pointer active:scale-98 hover:brightness-95 hover:saturate-95",
              )}
            >
              <RookieMissionBadgeCount />
              <div className="w-full h-auto aspect-square z-10 dark-green-panel p-0 flex flex-col items-center justify-center overflow-hidden">
                <img
                  src="https://cdn.navalabs.io/assets/newbie-mission_fKci0y.png"
                  alt="First Login Missions"
                  className="h-8"
                  style={{ imageRendering: "pixelated" }}
                />
                <span
                  className="-mt-3 text-xs text-white font-black uppercase leading-tight text-center"
                  style={{ textShadow: generateTextShadow(0.15) }}
                >
                  Newbie
                  <br />
                  Missions
                </span>
              </div>
            </button>
          </RookieMissionDialog>
        )}

        <LoginGate
          unauthenticatedLabel={
            <div className="w-full flex flex-col items-center justify-center">
              <div className="w-full h-auto aspect-square z-10 dark-green-panel p-0 relative flex items-center justify-center">
                <img
                  src="/assets/bag.png"
                  alt="Tournament Winner"
                  className="w-10"
                  style={{ imageRendering: "pixelated" }}
                />
                <div className="absolute inset-0 flex items-end justify-center z-10">
                  <span
                    className="text-xs text-white font-black uppercase leading-none text-center"
                    style={{ textShadow: generateTextShadow(0.15) }}
                  >
                    Social
                    <br />
                    Bonus
                  </span>
                </div>
              </div>
            </div>
          }
        >
          <button
            type="button"
            className={cn(
              "relative w-full flex flex-col items-center justify-center",
              "transition-transform duration-75 ease-linear",
              !shareMissions || shareMissions.length === 0
                ? "opacity-50 cursor-not-allowed hover:brightness-100 hover:saturate-100"
                : "hover:cursor-pointer active:scale-98 hover:brightness-95 hover:saturate-95",
            )}
            onClick={() => {
              trackSocialBonusClick({
                share_mission_count: shareMissions?.length ?? 0,
                pending_progress_count:
                  shareMissions?.reduce(
                    (acc, mission) =>
                      acc +
                      mission.progress.filter((p) => p.status === "pending")
                        .length,
                    0,
                  ) ?? 0,
              });
              setShowWinnerDialogs(true);
            }}
            disabled={!shareMissions || shareMissions.length === 0}
          >
            {shareMissions?.some((m) =>
              m.progress.some((p) => p.status === "pending"),
            ) && <PixelRedDot className="-top-1 -right-1" />}
            <div className="w-full h-auto aspect-square z-10 dark-green-panel p-0 relative flex items-center justify-center">
              <img
                src="/assets/bag.png"
                alt="Tournament Winner"
                className="w-14"
                style={{ imageRendering: "pixelated" }}
              />
              <div className="absolute inset-0 flex items-end justify-center z-10">
                <span
                  className="text-xs text-white font-black uppercase leading-none text-center"
                  style={{ textShadow: generateTextShadow(0.15) }}
                >
                  Social
                  <br />
                  Bonus
                </span>
              </div>
            </div>
          </button>
        </LoginGate>

        <button
          type="button"
          className={cn(
            "relative w-full flex flex-col items-center justify-center",
            "transition-transform duration-75 ease-linear",
            "hover:cursor-pointer active:scale-98 hover:brightness-95 hover:saturate-95",
          )}
          onClick={() => {
            if (!user) {
              login();
              return;
            }
            navigate({ to: "/event" });
          }}
        >
          <div className="w-full h-auto aspect-square z-10 dark-green-panel p-0 flex flex-col items-center justify-center">
            <img
              src="https://cdn.navalabs.io/icons/calendar_71Yoa.png"
              alt="Event"
              className="w-10 h-auto"
              style={{ imageRendering: "pixelated" }}
            />
            <span
              className="text-xs text-white font-black -mt-4 uppercase leading-none"
              style={{ textShadow: generateTextShadow(0.15) }}
            >
              {t("homeCanvas.eventCalendar.label")}
            </span>
          </div>
        </button>
      </div>

      {/* Center - Equipped Token Character */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <button
          data-step="lobby-equip-squad-slot"
          type="button"
          onClick={() => {
            setIsEquipSheetOpen(true);
            if (currentTour === "lobbySquadTour" && currentStep === 1) {
              setTimeout(() => setCurrentStep(2), 300);
            }
          }}
          className="relative flex flex-col items-center cursor-pointer active:scale-[0.99] transition-transform bg-transparent border-none p-0"
        >
          {effectiveEquipped ? (
            <div className="cfl-token-idle-stage">
              <div className="cfl-token-idle-glow" />
              <div className="cfl-token-idle-shadow" />
              <img
                src={effectiveEquipped.token.playerCard}
                alt={effectiveEquipped.token.tokenSymbol || "Equipped token"}
                className="cfl-token-idle-image w-56 h-56 sm:w-64 sm:h-64 object-contain"
                style={{ imageRendering: "pixelated" }}
              />
            </div>
          ) : (
            <img
              src="/assets/ref-unknown.png"
              alt="Equip token"
              className="w-56 h-56 sm:w-64 sm:h-64 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          )}
          {effectiveEquipped ? (
            <div
              className="relative -mt-5 sm:-mt-3 flex items-stretch border-2 border-black/60 rounded overflow-hidden shadow-[0_2px_0_rgba(0,0,0,0.4)]"
              style={{ imageRendering: "pixelated" }}
            >
              <div className="px-3 py-1 bg-zinc-900 flex items-center gap-1">
                <img
                  src={effectiveEquipped.token.tokenImageLogo}
                  alt=""
                  className="size-4 rounded-full"
                />
                <span
                  className="text-white text-sm font-black uppercase tracking-wider leading-none"
                  style={{ textShadow: generateTextShadow(0.5) }}
                >
                  {effectiveEquipped.token.tokenSymbol}
                </span>
              </div>
              <div
                className={cn(
                  "px-3 py-1 flex items-center gap-1",
                  effectiveEquipped.direction === "long"
                    ? "bg-emerald-600"
                    : "bg-red-600",
                )}
              >
                <span className="text-white text-sm font-black leading-none">
                  {effectiveEquipped.direction === "long" ? "▲" : "▼"}
                </span>
                <span
                  className="text-white text-sm font-black uppercase tracking-wider leading-none"
                  style={{ textShadow: generateTextShadow(0.5) }}
                >
                  {effectiveEquipped.direction}
                </span>
              </div>
            </div>
          ) : (
            <div
              className="relative -mt-5 sm:-mt-3 px-4 py-1 bg-zinc-900 border-2 border-black/60 rounded shadow-[0_2px_0_rgba(0,0,0,0.4)]"
              style={{ imageRendering: "pixelated" }}
            >
              <span
                className="text-white text-sm font-black uppercase tracking-wider leading-none"
                style={{ textShadow: generateTextShadow(0.5) }}
              >
                Tap to Pick
              </span>
            </div>
          )}
        </button>

        {/* Arena Container */}
        {(() => {
          const currentTrophies = statsData?.trophies ?? 0;
          const threshold = 100_000;
          const segments = 8;
          const progress = Math.min(currentTrophies / threshold, 1);
          const progressPct =
            currentTrophies > 0 ? Math.max(progress * 100, 1) : 0;
          return (
            <MilestoneSheet>
              <div className="relative w-3/5 md:w-[65%] max-w-[90%] mt-10 flex items-center gap-3 px-3 pb-2 bg-[#1D2A52]/80 border-2 border-black/60 rounded-lg cursor-pointer active:scale-[0.99] transition-transform shadow-[0_2px_0_rgba(0,0,0,0.4)]">
                <div className="absolute -left-3 -top-8 sm:-top-11 w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center overflow-hidden">
                  <img
                    src={
                      currentTrophies < threshold
                        ? "/assets/rookie-island-diorama.png"
                        : "/assets/pro-arena.png"
                    }
                    alt="Diorama"
                    className="w-full h-full object-contain"
                    style={{ imageRendering: "pixelated" }}
                  />
                </div>
                <div className="w-16 sm:w-20" />
                <div className="flex-1 flex flex-col min-w-0 leading-none gap-1.5">
                  <img
                    src={
                      currentTrophies < threshold
                        ? "https://cdn.navalabs.io/assets/badges/badge-rookie_j8hYXt.png"
                        : "https://cdn.navalabs.io/assets/badges/badge-pro_j8hYXt.png"
                    }
                    alt={currentTrophies < threshold ? "Rookie" : "Pro"}
                    className="h-8 w-auto self-start"
                    style={{ imageRendering: "pixelated" }}
                  />
                  <div className="flex items-center gap-1.5">
                    <img
                      src="https://cdn.navalabs.io/icons/trophy-icon_zozppj.png"
                      alt="trophy"
                      className="w-3 h-3 shrink-0"
                      style={{
                        imageRendering: "pixelated",
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
                      }}
                    />
                    <div className="flex-1 relative h-1.5 bg-black/50 rounded-sm overflow-hidden border border-black/30">
                      <div
                        className="h-full bg-gradient-to-r from-yellow-500 to-yellow-300 rounded-sm transition-all duration-500"
                        style={{ width: `${progressPct}%` }}
                      />
                      <div className="absolute inset-0 flex">
                        {Array.from({ length: segments + 1 }).map((_, i) => (
                          <div
                            key={i}
                            className="flex-1 border-r border-black/30 last:border-r-0"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </MilestoneSheet>
          );
        })()}

        {/* Play button with inline entry-fee badge */}
        <div className="relative flex items-center mt-6 gap-3 w-full">
          <DefaultButton
            type="button"
            aria-label="Game mode"
            className="absolute! -left-2 top-0 h-14! w-22 shrink-0 cursor-default text-white"
            data-step="lobby-modes-nav"
            onClick={() => {
              setTeleportDialogOpen(true);
              if (currentTour === "lobbyWelcomeTour" && currentStep === 1) {
                setTimeout(() => setCurrentStep(2), 300);
              }
            }}
          >
            <span className="relative z-10 flex h-full flex-col items-center justify-center gap-0.5">
              <img
                src="https://cdn.navalabs.io/components/navbar/modes_zz6UJT.png"
                className="w-14 -mt-4"
              />
              <span
                className="relative text-sm font-black uppercase leading-none -mt-4"
                style={{
                  textShadow: generateTextShadow(1),
                }}
              >
                Modes
              </span>
            </span>
          </DefaultButton>
          <DefaultButton
            data-step="practice-mode-button"
            onClick={() => {
              // Sponsored entries are pinned to $3 server-side — don't send
              // a tier on that flow.
              if (isSponsoredEligible) {
                void findMatch();
                return;
              }
              // Block before the on-chain tx if the user can't cover the
              // chosen tier — the BE-built tx would just fail at signing.
              if (typeof usdcBalance === "number" && usdcBalance < entryFee) {
                toast.error("Not enough USDC", {
                  description: `Need $${entryFee} to play — your balance is $${usdcBalance.toFixed(2)}.`,
                });
                return;
              }
              void findMatch(entryFee);
            }}
            disabled={playDisabled}
            variant={isSponsoredEligible ? "yellow" : "green"}
            className="w-full mx-25 uppercase h-14! text-sm sm:text-base md:text-lg"
          >
            {isSubmittingMatchmaking || isPreviewingSponsor ? (
              <span className="w-full flex items-center justify-center">
                <Spinner className="w-6 h-6" />
              </span>
            ) : (
              <span className="w-full flex items-center">
                <span className="flex-1 flex items-center justify-center">
                  Find Match
                </span>
                <span
                  aria-hidden="true"
                  className="shrink-0 w-0.5 h-8 ml-2 mr-1 bg-black/45 shadow-[1px_0_0_rgba(255,255,255,0.18)]"
                />
                {isSponsoredEligible ? (
                  <ShinyWavyText
                    text="FREE"
                    className="font-black text-sm sm:text-base md:text-lg uppercase tracking-wider -mt-2 shrink-0 pr-2"
                    dy={2}
                  />
                ) : (
                  <span className="shrink-0 inline-flex h-9 items-center justify-center gap-1 leading-none px-1.5">
                    <img
                      src={USDC_ICON}
                      alt="USDC"
                      className="w-4 h-4 block"
                      style={{ imageRendering: "pixelated" }}
                    />
                    <span className="text-white text-sm font-black leading-none -translate-y-px inline-block w-4 text-center tabular-nums">
                      {entryFee}
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className="size-3 -ml-0.5 text-white/85"
                    />
                  </span>
                )}
              </span>
            )}
          </DefaultButton>
          {!isSponsoredEligible && (
            <DropdownMenu
              open={isFeePopoverOpen}
              onOpenChange={setIsFeePopoverOpen}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Pick entry fee"
                  className="absolute right-[6.25rem] top-0 z-30 h-14 w-20 cursor-pointer bg-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="end"
                sideOffset={6}
                className="max-w-[16rem]"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <DropdownMenuLabel className="text-xs text-white/80 font-normal leading-snug whitespace-normal">
                  You'll be matched with another player who picks the same entry
                  fee.
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {[1, 3, 5, 10].map((v) => (
                  <DropdownMenuItem
                    key={v}
                    onSelect={() => setEntryFee(v)}
                    className="relative pl-9 pr-7 py-1.5 font-black cursor-pointer"
                  >
                    {entryFee === v && (
                      <span className="absolute left-2 flex size-3.5 items-center justify-center">
                        <img
                          src="https://cdn.navalabs.io/assets/arrow-select_yhEOlT.png"
                          alt=""
                          className="size-3"
                          style={{ imageRendering: "pixelated" }}
                        />
                      </span>
                    )}
                    <img
                      src={USDC_ICON}
                      alt=""
                      className="w-4 h-4"
                      style={{ imageRendering: "pixelated" }}
                    />
                    {v}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DefaultButton
            type="button"
            aria-label="Game mode"
            className="absolute! -right-2 top-0 h-13! w-22 shrink-0 cursor-default text-white p-0!"
            onClick={() => {
              navigate({ to: "/championship" });
            }}
          >
            <span className="relative z-10 flex h-full flex-col items-center justify-center gap-0.5">
              <img
                src={theme?.championship.trophyIcon}
                className="w-12 -mt-4"
              />
              <span
                className="relative text-sm font-black leading-none -mt-4"
                style={{
                  textShadow: generateTextShadow(1),
                }}
              >
                {/*in {theme?.championship.campaignDate.open}*/}
                {(() => {
                  const target = theme?.championship.campaignDate.started ?? 0;
                  const nowTs = dayjs().unix();
                  const dur = dayjs.duration(target - nowTs, "seconds");
                  const days = Math.floor(dur.asDays());
                  const hours = dur.hours();
                  const minutes = dur.minutes();
                  if (dur.days() <= 0) {
                    return `in ${Math.floor(dur.asHours())}h ${minutes}m`;
                  }
                  if (dur.asSeconds() >= 0) {
                    return `in ${days}d ${hours}h`;
                  } else {
                    return theme?.championship.title;
                  }
                })()}
              </span>
            </span>
          </DefaultButton>
        </div>

        {/* 1v1 history — plain underlined text link under Find Match */}
        <button
          type="button"
          onClick={() => navigate({ to: "/match/history" })}
          className="mt-2 text-xs font-bold uppercase text-white underline tracking-wider hover:cursor-pointer active:scale-95"
        >
          1v1 History
        </button>
      </div>

      {/* Equip Token Sheet */}
      <Sheet
        open={isEquipSheetOpen}
        onOpenChange={(open) => {
          if (!open && currentTour === "lobbySquadTour") return;
          setIsEquipSheetOpen(open);
        }}
      >
        <SheetContent
          side="bottom"
          onInteractOutside={(e) => {
            if (currentTour === "lobbySquadTour") e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (currentTour === "lobbySquadTour") e.preventDefault();
          }}
          className="bg-[#1D2A52] border-t-2 border-[#3a4f8a] rounded-t-2xl flex flex-col max-h-[85svh] px-2 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <EquipTokenSheetContent
            onEquipped={() => setIsEquipSheetOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Social Bonus Winner Dialogs */}
      {/* Sponsored 1v1 pre-commit confirm. Dismissing is a true no-op — the
          on-chain match is only created when the user clicks Find Match
          inside the dialog. */}
      <SponsorDialog
        open={isFreeConfirmOpen}
        onOpenChange={(open) => {
          if (!open) dismissFreeConfirm();
        }}
        sponsor={
          previewedSponsor
            ? {
                name: previewedSponsor.name,
                wordmark: previewedSponsor.name,
                logoUrl: previewedSponsor.image_url,
                description: previewedSponsor.description,
              }
            : undefined
        }
        onConfirm={() => void confirmFreeMatch()}
        confirmLabel="Find Match"
        isConfirming={isSubmittingMatchmaking}
      />

      {showWinnerDialogs &&
        shareMissions &&
        shareMissions.length > 0 &&
        statsData?.referralCode && (
          <TournamentWinnerDialogs
            missions={shareMissions}
            refCode={statsData.referralCode}
            hasXConnected={hasXConnected}
            onComplete={() => setShowWinnerDialogs(false)}
            onRefetch={() => refetchShareMissions()}
          />
        )}

      {/* Onboarding dialogs */}
      {!user && (
        <StepDialog
          isOpen={isOnboardingDialogOpen}
          steps={getOnboardingLoggedOutDialogSteps()}
          preventOutsideDismiss={true}
          mode="fullscreen"
          onClose={() => setIsLoggedOutOnboardingConsumed(true)}
          onLastAction={handleLoggedOutOnboardingAction}
          lastStepLoading={isLoginPending}
          lastStepLoadingLabel={t("stepDialog.signingIn")}
          lastStepError={loginError}
        />
      )}
      {user && (
        <StepDialog
          isOpen={isPostLoginDialogOpen}
          steps={getOnboardingLoggedInDialogSteps()}
          shouldShowHeaderText={true}
          preventOutsideDismiss={true}
          onClose={() => setIsPostLoginDialogOpen(false)}
          onLastAction={() => {
            if (firstLoginDialogKey) {
              try {
                localStorage.setItem(firstLoginDialogKey, "1");
              } catch {
                // Ignore storage failures; the dialog can still be dismissed for this session.
              }
            }
            setIsPostLoginDialogOpen(false);
          }}
        />
      )}

      {/* Matchmaking floating pills — one per active seed (multi-match) */}
      <MatchmakingPills />
    </div>
  );
}
